import { z } from 'zod';

export type DocumentLocationLink = `#doc/${string}/ver/${string}/loc/${string}`;

const DOCUMENT_CITATION_PATTERN = /\]\(#doc\/([^/)]+)\//g;

/**
 * Whether `reasoning` contains at least one markdown location link, the
 * signal that it should render as markdown with clickable citations rather
 * than plain text. Same-document citations count too, and so do older
 * answers whose citations sit in a trailing block: both use this syntax.
 */
export function getHasDocumentCitations(reasoning: string): boolean {
  return reasoning.includes('](#doc/');
}

/**
 * Distinct documents cited by `#doc/` links in `reasoning`, excluding
 * `excludeDocumentId` (the answering document itself).
 */
export function countRelatedDocumentCitations(
  reasoning: string,
  excludeDocumentId?: string
): number {
  const ids = new Set(Array.from(reasoning.matchAll(DOCUMENT_CITATION_PATTERN), m => m[1]));
  if (excludeDocumentId) ids.delete(excludeDocumentId);

  return ids.size;
}

export function getRelationshipCountTooltip(count: number): string {
  return `${count} ${count === 1 ? 'Relationship' : 'Relationships'}`;
}

/** The label a resolved citation gets: `[Citation](#doc/...)`. */
export const CITATION_LINK_LABEL = 'Citation';

/**
 * Creates a markdown location link in the format:
 *
 * #doc/<documentId>/ver/<versionId>/loc/<locationAnchor>
 */
export function createMarkdownLocationLink(
  documentId: string,
  versionId: string,
  locationAnchor: string
): DocumentLocationLink {
  return `#doc/${documentId}/ver/${versionId}/loc/${locationAnchor}`;
}

/**
 * Escapes `text` for the title slot of a markdown link, the quoted string in
 * `[label](href "title")`. Backslash goes first so a literal backslash in the
 * source is not misread as escaping the quote after it. Link labels use a
 * different rule and are not covered here.
 */
export function escapeMarkdownLinkTitle(text: string): string {
  return text.replaceAll('\\', '\\\\').replaceAll('"', '\\"');
}

const locationSchema = z.tuple([
  z.literal('#doc'),
  z.string(),
  z.literal('ver'),
  z.string(),
  z.literal('loc'),
  z.string(),
]);

/**
 * Parses a markdown location link in the format:
 *
 * #doc/<documentId>/ver/<versionId>/loc/<locationAnchor>
 */
export function parseMarkdownLocationLink(
  locationString: string
): { documentId: string; locationId: string; type: 'file' | 'aline'; versionId: string } | null {
  const parts = locationString.split('/');

  const result = locationSchema.safeParse(parts);
  if (!result.success) {
    console.error('Invalid file location format:', locationString);
    return null;
  }

  const [, documentId, , versionId, , locationId] = result.data;

  const type = locationId.includes(':') ? 'file' : 'aline';

  return {
    documentId,
    locationId,
    type,
    versionId,
  };
}
